'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, List, FlaskConical, ChevronLeft, ChevronRight, LogOut, Bell, RefreshCw, Users, BookOpen, ClipboardList, UserCircle, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import AppLogo from './ui/AppLogo';
import NotificationsPanel from './NotificationsPanel';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const mainNavItems = [
  { href: '/', label: 'Tableau de bord', icon: LayoutDashboard },
  { href: '/print-jobs-list', label: 'Impressions', icon: List },
  { href: '/passeports', label: 'Étiquettes & passeports', icon: BookOpen },
  { href: '/patients', label: 'Patients', icon: Users },
];

const settingsNavItems = [
  { href: '/shining-sync', label: 'Sync SHINING', icon: RefreshCw },
  { href: '/resin-lots-management', label: 'Lots résine', icon: FlaskConical },
  { href: '/traceability-register', label: 'Registre global', icon: ClipboardList },
];

function getInitials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return 'U';
}

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileRole, setProfileRole] = useState('');
  const [profileAvatar, setProfileAvatar] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    if (!user) return;
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('full_name, role_title, avatar_url')
        .eq('id', user.id)
        .maybeSingle();
      if (data) {
        setProfileName(data.full_name || user.email || '');
        setProfileRole(data.role_title || 'Praticien');
        setProfileAvatar(data.avatar_url || null);
      } else {
        setProfileName(user.email || '');
        setProfileRole('Praticien');
      }
    };
    fetchProfile();
  }, [user]);

  // Auto-open settings section if current path is a settings item
  useEffect(() => {
    const isSettingsPath = settingsNavItems.some(item => pathname.startsWith(item.href));
    if (isSettingsPath) setSettingsOpen(true);
  }, [pathname]);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const displayName = profileName || user?.email || 'Utilisateur';
  const initials = getInitials(displayName);

  const NavItem = ({ item, isCollapsed }: { item: typeof mainNavItems[0]; isCollapsed: boolean }) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <li>
        <Link
          href={item.href}
          className={`sidebar-nav-item ${active ? 'active' : ''} ${isCollapsed ? 'justify-center px-2' : ''}`}
          title={isCollapsed ? item.label : undefined}
        >
          <Icon size={18} className="flex-shrink-0" />
          {!isCollapsed && <span className="flex-1 truncate">{item.label}</span>}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out border-r ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {/* Logo */}
        <div className={`flex items-center border-b h-16 px-3 ${collapsed ? 'justify-center' : 'gap-3 px-4'}`} style={{ borderColor: 'var(--border)' }}>
          <AppLogo size={32} />
          {!collapsed && (
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-sm leading-tight" style={{ color: 'var(--foreground)' }}>PrintTrace</span>
              <span className="text-2xs truncate" style={{ color: 'var(--muted-foreground)' }}>AccuFab F1</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {/* Main items */}
          <ul className="space-y-0.5">
            {mainNavItems.map((item) => (
              <NavItem key={item.href} item={item} isCollapsed={collapsed} />
            ))}
          </ul>

          {/* Separator */}
          <div className="my-3 border-t" style={{ borderColor: 'var(--border)' }} />

          {/* Paramètres / Réglages collapsible section */}
          {collapsed ? (
            // Collapsed: show settings icons directly with a small settings icon above
            <ul className="space-y-0.5">
              <li>
                <button
                  onClick={() => { onToggleCollapse(); }}
                  className="sidebar-nav-item w-full justify-center px-2"
                  title="Réglages"
                >
                  <Settings size={18} className="flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
                </button>
              </li>
              {settingsNavItems.map((item) => (
                <NavItem key={item.href} item={item} isCollapsed={true} />
              ))}
            </ul>
          ) : (
            <div>
              <button
                onClick={() => setSettingsOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors hover:bg-muted group"
              >
                <div className="flex items-center gap-2">
                  <Settings size={15} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="section-header" style={{ marginBottom: 0 }}>Réglages</span>
                </div>
                {settingsOpen
                  ? <ChevronUp size={13} style={{ color: 'var(--muted-foreground)' }} />
                  : <ChevronDown size={13} style={{ color: 'var(--muted-foreground)' }} />
                }
              </button>
              {settingsOpen && (
                <ul className="space-y-0.5 mt-1 ml-1">
                  {settingsNavItems.map((item) => (
                    <NavItem key={item.href} item={item} isCollapsed={false} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </nav>

        {/* Bottom section */}
        <div className="border-t px-2 py-3 space-y-1" style={{ borderColor: 'var(--border)' }}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg mb-2" style={{ backgroundColor: 'var(--surface)' }}>
              {profileAvatar ? (
                <img
                  src={profileAvatar}
                  alt="Avatar"
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 truncate" style={{ color: 'var(--foreground)' }}>{displayName}</p>
                <p className="text-2xs truncate" style={{ color: 'var(--muted-foreground)' }}>{profileRole}</p>
              </div>
              <div className="relative">
                <button
                  onClick={() => setNotifOpen(v => !v)}
                  className="relative p-1 rounded hover:bg-muted transition-colors"
                  title="Notifications"
                >
                  <Bell size={14} style={{ color: unreadCount > 0 ? 'var(--primary)' : 'var(--muted-foreground)' }} />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold"
                      style={{ backgroundColor: 'var(--danger, #ef4444)', color: 'white', fontSize: '8px' }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
              </div>
            </div>
          )}

          <Link
            href="/account"
            className={`sidebar-nav-item ${isActive('/account') ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
            title={collapsed ? 'Mon compte' : undefined}
          >
            <UserCircle size={16} className="flex-shrink-0" />
            {!collapsed && <span>Mon compte</span>}
          </Link>

          <Link
            href="/login"
            className={`sidebar-nav-item ${collapsed ? 'justify-center px-2' : ''}`}
            title={collapsed ? 'Déconnexion' : undefined}
          >
            <LogOut size={16} className="flex-shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </Link>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center h-8 border-t transition-colors hover:bg-muted"
          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
          aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col lg:hidden transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: 'var(--card)', borderRight: '1px solid var(--border)' }}
      >
        <div className="flex items-center justify-between border-b h-16 px-4" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-sm">PrintTrace</span>
          </div>
          <button onClick={onMobileClose} className="p-1.5 rounded hover:bg-muted">
            <ChevronLeft size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {/* Main items */}
          <ul className="space-y-0.5">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <li key={`mobile-${item.href}`}>
                  <Link
                    href={item.href}
                    className={`sidebar-nav-item ${active ? 'active' : ''}`}
                    onClick={onMobileClose}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="my-3 border-t" style={{ borderColor: 'var(--border)' }} />

          {/* Réglages section */}
          <div>
            <button
              onClick={() => setSettingsOpen(v => !v)}
              className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-colors hover:bg-muted"
            >
              <div className="flex items-center gap-2">
                <Settings size={15} style={{ color: 'var(--muted-foreground)' }} />
                <span className="section-header" style={{ marginBottom: 0 }}>Réglages</span>
              </div>
              {settingsOpen
                ? <ChevronUp size={13} style={{ color: 'var(--muted-foreground)' }} />
                : <ChevronDown size={13} style={{ color: 'var(--muted-foreground)' }} />
              }
            </button>
            {settingsOpen && (
              <ul className="space-y-0.5 mt-1 ml-1">
                {settingsNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <li key={`mobile-settings-${item.href}`}>
                      <Link
                        href={item.href}
                        className={`sidebar-nav-item ${active ? 'active' : ''}`}
                        onClick={onMobileClose}
                      >
                        <Icon size={18} className="flex-shrink-0" />
                        <span className="flex-1 truncate">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </nav>

        <div className="border-t px-2 py-3 space-y-1" style={{ borderColor: 'var(--border)' }}>
          <Link href="/account" className={`sidebar-nav-item ${isActive('/account') ? 'active' : ''}`} onClick={onMobileClose}>
            <UserCircle size={16} className="flex-shrink-0" />
            <span>Mon compte</span>
          </Link>
          <Link href="/login" className="sidebar-nav-item" onClick={onMobileClose}>
            <LogOut size={16} className="flex-shrink-0" />
            <span>Déconnexion</span>
          </Link>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}
    </>
  );
}